import React, {useEffect} from "react";
import {Input} from "reactstrap";
import {formatDate} from "../../script/commFunc";
import styled from "styled-components";
import {useCallback, useRef} from "react";
import {BsChevronLeft, BsChevronRight} from "react-icons/bs";
import moment from "moment";

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 4px;
`;

const Icons = styled.div`
  align-self: center;
  padding: 0px 5px;
`;

const HSeparator = styled.div`
  margin: 0 3px;
  align-self: center;
`;

const InputWrapper = styled.div`
  width: 100%;
  max-width: 110px;
`;

/**
 *
 * @User : 백욱진
 * @Date : 2023.05.08
 * @file : Components/Common/CpmCommDateFromToWeek.js
 * @Desc : 타입별 데이트 레인지 픽커 컴포넌트
 * @param {Array<string>} id - id값 (ex ['perfStDt', 'perfEdDt']) (부모 컴포넌트의 파라미터 key 값으로도 사용)
 * @param {string} type 컴포넌트 형식 "week" | "month" | "year" | "weekmon"
 * @param {object} params - 부모 컴포넌트로부터 받아온 파라미터
 * @param {React.SetStateAction} setParams - 부모 컴포넌트로부터 받아온 파라미터 세터
 * @param {string} label - 라벨 명
 * @param {boolean} essential - 필수 여부
 * @param {boolean} isDisabled - disabled 여부
 * @param {string} maxDate - 입력할 수 있는 최대 날짜
 * @param {string} minDate - 입력할 수 있는 최소 날짜
 * @param {string} diffMon - stDT와 edDt의 월 차이 
*/

const CpmCommDateFromToWeek = ({
    id = [], 
    type, 
    params, 
    setParams,
    label,
    essential = false,
    isDisabled,
    maxDate,
    minDate,
    diffMon,
}) => {
    const stNm = id[0] || 'perfStDt';
    const edNm = id[1] || 'perfEdDt';
    const diffMonth = diffMon - 1; //stDT와 edDt의 월 차이 
    const paramsFormat = "yyMMDD";
    const localFormat = "yyyy-MM-DD";
    const today = formatDate(new Date(), paramsFormat);
    const perfStDtRef = useRef(null); // perfStDt element에 접근 제어
    const perfEdDtRef = useRef(null); // perfEdDt element에 접근 제어

    if (!type) {
        type = 'null';
    }

    const changeDates = (refFrom, refTo, func) => {
        const newDates = {
            null: params[stNm] ? // type === null, 시작날짜는 그대로, 끝날짜는 값이 있다면 그대로, 없다면 달의 마지막 날
                { 
                    from: moment(refFrom), to: moment(refTo),
                }:{ 
                    from: moment(refFrom).startOf("month"), to: moment(refTo).endOf("month"),
            }, week: { // type === week, 시작날짜는 주의 시작, 끝날짜는 주의 끝
                from: moment(refFrom).startOf("week"), to: moment(refTo).endOf("week"),
            }, month: { // type === month, 시작날짜는 달의 시작, 끝날짜는 달의 끝
                from: moment(refFrom).startOf("month"), to: moment(refTo).endOf("month"),
            }, year: { // type === year, 시작날짜는 년의 시작, 끝날짜는 년의 끝
                from: moment(refFrom).startOf("month"), to: moment(refTo).endOf("month"),
            }, weekmon: { // type === weekmon, 시작날짜는 주의 시작, 끝날짜는 주의 끝
                from: moment(refFrom).startOf("week").add(1, "day"),
                to: moment(refFrom).add(3, "week").endOf("week").add(1, "day"),
            },
        };

        // 바뀐 날짜 값으로 param 변경 
        setParams({
            [stNm]: func(newDates[type]?.from), [edNm]: func(newDates[type]?.to)
        });
    };

    // 날짜 증가
    const handleIncrease = () => {
        const TYPE = type === "weekmon" ? "week" : type;
        changeDates(params[stNm], params[edNm], (date) => moment(date).add(1, TYPE).format(paramsFormat));
    };

    // 날짜 감소
    const handleDecrease = () => {
        const TYPE = type === "weekmon" ? "week" : type;
        changeDates(params[stNm], params[edNm], (date) => moment(date).subtract(1, TYPE).format(paramsFormat));
    };

    // 시작 날짜 변경 감지
    const handleChangeFrom = (e) => {
        if (type === "year") {
            setParams({[stNm]: moment(e.target.value).format(paramsFormat)});
        } else {
            if (moment( e.target.value).isSameOrBefore( params[edNm])) {
                changeDates(e.target.value, params[edNm], (date) => moment(date).format(paramsFormat));
            }else{
                setParams({ [edNm]: e.target.value });
                changeDates(e.target.value, e.target.value, (date) => moment(date).format(paramsFormat));
            }
        }
    };

    // 끝 날짜 변경 감지
    const handleChangeTo = (e) => {
        if (type === "year") {
            setParams({[edNm]: moment(e.target.value).format(paramsFormat)});
        } else {
            if (moment( e.target.value).isSameOrAfter( params[stNm])) {
                changeDates(params[stNm], e.target.value, (date) => moment(date).format(paramsFormat));
            } else{
                setParams({ [stNm]: e.target.value });
                changeDates(e.target.value, e.target.value, (date) => moment(date).format(paramsFormat));
            }
        }
    };

    const initialize = useCallback(() => {
        changeDates(today, today, (date) => moment(date).format(paramsFormat))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [today]);

    useEffect(() => {
        if(!params[stNm] || !params[edNm]){
            initialize();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (<Wrapper>
            <div className="d-flex">
                {type !== 'null' && (<Icons>
                        <BsChevronLeft
                            className="cs-c"
                            size={14}
                            onClick={handleDecrease}
                        />
                    </Icons>)}
                <InputWrapper>
                    <Input
                        name={label || stNm}
                        type="date"
                        bsSize="sm"
                        className={`${essential && 'essential'} form-control`}
                        value={formatDate(params[stNm], localFormat)}
                        onChange={handleChangeFrom}
                        ref={perfStDtRef}
                        max={formatDate(
                                Math.min(
                                    maxDate || '99991231',
                                ).toString(),
                                localFormat
                            )}
                        min={formatDate(
                                Math.max(
                                    diffMonth ? moment(params[edNm]).subtract(diffMonth, 'month').startOf("month").format(paramsFormat) : '10000101', 
                                    minDate || '10000101',
                                ).toString(), 
                                localFormat
                            )}
                        disabled={isDisabled}
                    />
                </InputWrapper>
            </div>

            <HSeparator>~</HSeparator>

            <div className="d-flex">
                <InputWrapper>
                    <Input
                        name={label || edNm}
                        type="date"
                        bsSize="sm"
                        className={`${essential && 'essential'} form-control`}
                        value={formatDate(params[edNm], localFormat)}
                        onChange={handleChangeTo}
                        ref={perfEdDtRef}
                        max={formatDate(
                            Math.min(
                                diffMonth ? moment(params[stNm]).add(diffMonth, 'month').endOf("month").format(paramsFormat) : '99991231', 
                                maxDate || '99991231',
                            ).toString(),
                            localFormat
                        )}
                        min={formatDate(
                            Math.max(
                                minDate || '10000101',
                            ).toString(), 
                            localFormat
                        )}
                        disabled={isDisabled}
                    />
                </InputWrapper>
                {type !== 'null' && (<Icons>
                        <BsChevronRight
                            className="cs-p"
                            size={12}
                            onClick={handleIncrease}
                        />
                    </Icons>)}
            </div>
        </Wrapper>);
};

export default CpmCommDateFromToWeek;